import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import "./UserProfile.css";

/* BACKEND URL */
const API =
  process.env.REACT_APP_API_URL ||
  "https://backend-9i6n.onrender.com";

/* ---------- inline icon set (green gov. theme) ---------- */
const Icon = {
  mail: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  ),
  phone: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2 4.2 2 2 0 0 1 4 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L7.6 9.8a16 16 0 0 0 6 6l1.4-1.5a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6A2 2 0 0 1 22 16.9z" />
    </svg>
  ),
  pin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ),
  doc: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M9 13h6M9 17h6" />
    </svg>
  ),
  total: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <path d="m9 11 3 3L22 4" />
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  ),
  x: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
      <circle cx="12" cy="12" r="10" />
      <path d="m15 9-6 6M9 9l6 6" />
    </svg>
  ),
  logout: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5M21 12H9" />
    </svg>
  ),
};

/* ---------- helpers ---------- */
const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("") || "न";

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const FILTERS = [
  { key: "all", label: "सर्व" },
  { key: "approved", label: "मंजूर" },
  { key: "pending", label: "प्रलंबित" },
  { key: "rejected", label: "नाकारलेले" },
];

const UserProfile = () => {
  const { token, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [filter, setFilter] = useState("all");

  /* ================= FETCH PROFILE ================= */
  const fetchProfile = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${API}/api/user/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setProfile(res.data);
    } catch (err) {
      console.error("Profile fetch error:", err);
      if (err.response?.status === 401) {
        logout();
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (token) fetchProfile();
  }, [token]);

  /* ================= CANCEL APPLICATION ================= */
  const cancelApplication = async (id) => {
    if (!window.confirm("ही अर्ज रद्द करायची आहे का?")) return;

    try {
      setDeletingId(id);

      await axios.delete(`${API}/api/services/application/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      fetchProfile(); // refresh list
    } catch (err) {
      alert("अर्ज रद्द करता आला नाही ❌");
    } finally {
      setDeletingId(null);
    }
  };

  /* ================= RE-APPLY ================= */
  const reApply = (serviceType) => {
    navigate(`/apply-service?service=${encodeURIComponent(serviceType)}`);
  };

  /* ================= LOADING (skeleton) ================= */
  if (loading) {
    return (
      <div className="up-page">
        <section className="rk-hero up-hero">
          <div className="rk-hero-inner">
            <div className="rk-crumb">मुख्यपृष्ठ / माझे खाते</div>
          </div>
        </section>
        <div className="up-wrap">
          <div className="up-card up-identity">
            <div className="sk sk-avatar" />
            <div className="up-identity-lines">
              <div className="sk" style={{ width: 160, height: 22, borderRadius: 6 }} />
              <div className="sk" style={{ width: 110, height: 13, borderRadius: 6, marginTop: 10 }} />
            </div>
          </div>
          <div className="up-card">
            <div className="up-grid">
              {[0, 1, 2, 3].map((i) => (
                <div className="up-field" key={i}>
                  <div className="sk" style={{ width: 70, height: 11, borderRadius: 5 }} />
                  <div className="sk" style={{ width: 130, height: 15, borderRadius: 5, marginTop: 8 }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const { user, services = [] } = profile;

  /* ---------- derived stats ---------- */
  const counts = services.reduce(
    (acc, s) => {
      const st = s.status?.toLowerCase();
      acc.total += 1;
      if (st === "approved") acc.approved += 1;
      else if (st === "rejected") acc.rejected += 1;
      else acc.pending += 1;
      return acc;
    },
    { total: 0, approved: 0, pending: 0, rejected: 0 }
  );

  const STATS = [
    { key: "total", icon: Icon.total, label: "एकूण अर्ज", value: counts.total, cls: "ink" },
    { key: "approved", icon: Icon.check, label: "मंजूर", value: counts.approved, cls: "green" },
    { key: "pending", icon: Icon.clock, label: "प्रलंबित", value: counts.pending, cls: "amber" },
    { key: "rejected", icon: Icon.x, label: "नाकारलेले", value: counts.rejected, cls: "red" },
  ];

  const infoItems = [
    { key: "email", icon: Icon.mail, label: "ईमेल", value: user.email || "—" },
    { key: "mobile", icon: Icon.phone, label: "मोबाईल", value: user.mobile || "—" },
    { key: "village", icon: Icon.pin, label: "गाव", value: user.village || "—" },
    {
      key: "joined",
      icon: Icon.calendar,
      label: "नोंदणी दिनांक",
      value: user.createdAt ? formatDate(user.createdAt) : "—",
    },
  ];

  const shown =
    filter === "all"
      ? services
      : services.filter((s) => s.status?.toLowerCase() === filter);

  return (
    <div className="up-page">
      {/* ================= HERO BANNER ================= */}
      <section className="rk-hero up-hero">
        <div className="rk-hero-inner">
          <div className="rk-crumb">मुख्यपृष्ठ / माझे खाते</div>
        </div>
      </section>

      <div className="up-wrap">
        {/* ================= IDENTITY CARD ================= */}
        <header className="up-card up-identity">
          <div className="up-avatar">{getInitials(user.fullName)}</div>

          <div className="up-identity-main">
            <h1 className="up-name">{user.fullName}</h1>
            <div className="up-sub-row">
              <span>नागरिक</span>
              <span className="up-dot" />
              <span className="up-sub-item">
                {Icon.pin} {user.village || "—"}
              </span>
            </div>
          </div>

          <button
            className="up-logout"
            onClick={() => {
              logout();
              navigate("/login");
            }}
          >
            {Icon.logout} बाहेर पडा
          </button>
        </header>

        {/* ================= STATS ================= */}
        <div className="up-stats-grid">
          {STATS.map((s) => (
            <div className="up-card up-stat" key={s.key}>
              <span className={`up-stat-icon up-stat-icon--${s.cls}`}>{s.icon}</span>
              <div>
                <div className={`up-stat-num up-stat-num--${s.cls}`}>{s.value}</div>
                <div className="up-stat-label">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ================= ACCOUNT INFO ================= */}
        <section className="up-card">
          <div className="up-section-head">
            <span className="up-section-icon">{Icon.doc}</span>
            <h2 className="up-title">खात्याची माहिती</h2>
          </div>
          <div className="up-grid">
            {infoItems.map((item) => (
              <div className="up-field" key={item.key}>
                <span className="up-field-label">
                  <span className="up-field-icon">{item.icon}</span>
                  {item.label}
                </span>
                <span className="up-field-value">{item.value}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ================= APPLICATIONS ================= */}
        <div className="up-apps-head">
          <div className="up-apps-head-left">
            <h2 className="up-title">माझे अर्ज</h2>
            {services.length > 0 && <span className="up-count">{counts.total}</span>}
          </div>
          {services.length > 0 && (
            <div className="up-filters">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  className={`up-filter-chip ${filter === f.key ? "active" : ""}`}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {services.length === 0 ? (
          <div className="up-card up-empty">
            <span className="up-empty-icon">{Icon.doc}</span>
            <h3>अद्याप कोणतेही अर्ज नाहीत</h3>
            <p>आपण दाखल केलेले प्रमाणपत्र व सेवांचे अर्ज येथे दिसतील.</p>
            <button className="up-btn up-btn-primary" onClick={() => navigate("/services")}>
              सेवा पहा
            </button>
          </div>
        ) : shown.length === 0 ? (
          <div className="up-card up-empty">
            <span className="up-empty-icon">{Icon.doc}</span>
            <h3>या वर्गात कोणतेही अर्ज नाहीत</h3>
            <p>आपण दाखल केलेले प्रमाणपत्र व सेवांचे अर्ज येथे दिसतील.</p>
            <button className="up-btn up-btn-primary" onClick={() => navigate("/services")}>
              सेवा पहा
            </button>
          </div>
        ) : (
          <div className="up-apps">
            {shown.map((s) => {
              const st = s.status?.toLowerCase();

              return (
                <article key={s._id} className={`up-app up-app-${st}`}>
                  <div className="up-app-row">
                    <span className="up-app-icon">{Icon.doc}</span>
                    <div className="up-app-titles">
                      <h3 className="up-app-name">{s.serviceType}</h3>
                      {s.createdAt && (
                        <span className="up-app-date">
                          अर्ज दिनांक : {formatDate(s.createdAt)}
                        </span>
                      )}
                    </div>
                    <span className={`up-badge up-badge-${st}`}>{s.status}</span>
                  </div>

                  {s.adminRemark && (
                    <div className={`up-remark up-remark-${st}`}>
                      <span className="up-remark-label">
                        {s.status === "Approved" ? "संदेश" : "कारण"}
                      </span>
                      <p>{s.adminRemark}</p>
                    </div>
                  )}

                  {(s.status === "Rejected" || s.status === "Pending") && (
                    <div className="up-app-actions">
                      {s.status === "Pending" && (
                        <button
                          className="up-btn up-btn-ghost"
                          onClick={() => cancelApplication(s._id)}
                          disabled={deletingId === s._id}
                        >
                          {deletingId === s._id ? "रद्द करत आहे…" : "अर्ज रद्द करा"}
                        </button>
                      )}
                      {s.status === "Rejected" && (
                        <button
                          className="up-btn up-btn-primary"
                          onClick={() => reApply(s.serviceType)}
                        >
                          पुन्हा अर्ज करा
                        </button>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
